import { deleteEmployeeBeneficiary } from '@api/apiCall';
import { Button, View } from '@components/primitives';
import useFetchBanks from '@customHooks/useFetchBanks';
import { useToast } from '@hooks';
import BankList from '@molecules/BankList';
import DefaultBankHeader from '@molecules/DefaultBankHeader';
import BottomSheet from '@molecules/LoginInfoSheet';
import PinWithAddBank from '@molecules/PinWithAddBank';
import { shouldShowError } from '@organisms/HomeTab/homeUtils';
import { useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import appStyles from '@root/appStyles';
import { BOTTOMSHEET_CONTENT, BOTTONSHEET_KEYS, COMMON_CONSTANTS, addBankTemplate3 } from '@root/constants';
import { getStore } from '@store/storeUtils';
import { useMutation } from '@tanstack/react-query';
import colors from '@theme/colors';
import AmplitudeHelper from '@utils/analytics';
import { AMPLITUDE_CONSTANTS, AMPLITUDE_EVENT_SOURCES } from '@utils/analytics/constants';
import { verticalScale } from '@utils/normalize';
import { returnBottomSheetContent } from '@utils/utils';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking, ScrollView } from 'react-native';
import bankListBottomSheetHandlers from './bankListWithHeaderUtils';

function shouldShowFooter(
  t: (key: string) => string,
  fromTransfer: boolean,
  bankList: Array<{ status: string }> | undefined,
  source: string,
) {
  if (fromTransfer) {
    return null;
  }

  const pendingList = bankList?.filter(item => item.status === 'PENDING') ?? [];
  const hasPendingBanks = pendingList.length > 0;

  const openWhatsappOnPress = () => {
    const e = AMPLITUDE_CONSTANTS.bankRegistration.help_clicked;
    const { name, attributes } = e;
    AmplitudeHelper.logEvent(name, {
      [attributes.source]: source,
    });
    Linking.openURL(COMMON_CONSTANTS.support);
  };

  return (
    <View
      flexDirection="row"
      justifyContent="space-evenly"
      style={[
        appStyles.borderWithShadow,
        appStyles.marginHorizontal(0),
        appStyles.paddingHorizontal(16),
        appStyles.borderBottomLeftRadius(0),
        appStyles.borderBottomRightRadius(0),
        appStyles.paddingVertical(16),
      ]}>
      {hasPendingBanks ? (
        <Button
          variant="ghost"
          flex={1}
          borderColor={colors.variants.clover}
          style={[appStyles.borderWidth(1), appStyles.marginRight(8)]}
          onPress={openWhatsappOnPress}
          paddingY={verticalScale(8)}
          _text={{ color: colors.variants.clover }}>
          {t('phoneTransfer.phoneTransferSupport')}
        </Button>
      ) : null}
      <PinWithAddBank
        type={addBankTemplate3}
        userBanks={bankList}
        fromTransfer
        source={AMPLITUDE_EVENT_SOURCES.bank_page}
      />
    </View>
  );
}

type InfoModalType = {
  visible: boolean;
  data: any;
  errorCode: string;
};

function GGBankListWithDefaultBank(): React.JSX.Element {
  const initialState: InfoModalType = {
    visible: false,
    data: returnBottomSheetContent(BOTTONSHEET_KEYS.BANK_DELETE, BOTTOMSHEET_CONTENT[BOTTONSHEET_KEYS.BANK_DELETE]),
    errorCode: BOTTONSHEET_KEYS.BANK_DELETE,
  };

  const [infoModal, setInfoModal] = useState<InfoModalType>(initialState);
  const route = useRoute<any>();
  const { t } = useTranslation('common');
  const { fromTransfer, setBankAccountNumber } = route.params;
  const { setLoading, setInitialData } = getStore();
  const toast = useToast();
  const { bankList, refetch } = useFetchBanks();
  const isFocused = useIsFocused();

  const [bankRowPressed] = useState<any>(null);
  const navigation = useNavigation<any>();

  const {
    isLoading: isLoadingDelete,
    isSuccess: isSuccessDelete,
    isError: isErrorDelete,
    error: errorDelete,
    data: dataDelete,
    mutate: beneficiaryDeleteMutate,
  } = useMutation({
    mutationFn: (payload: any) => deleteEmployeeBeneficiary(payload),
  });

  useEffect(() => {
    navigation.setOptions({
      title: t(fromTransfer ? 'addBank.bankHeaderFromTransfer' : 'addBank.bankList'),
    });
  }, []);

  useEffect(() => {
    setLoading(isLoadingDelete);
  }, [isLoadingDelete]);

  useEffect(() => {
    shouldShowError(isErrorDelete, errorDelete, toast, t, navigation, setInitialData);
  }, [isErrorDelete, errorDelete]);

  useEffect(() => {
    if (isFocused) {
      refetch();
    }
  }, [isFocused]);

  useEffect(() => {
    if (isSuccessDelete && dataDelete) {
      refetch();
    }
  }, [isSuccessDelete, dataDelete]);

  const { onPressPrimaryBottomSHeet, onPressSecondaryBottomSheet, onCloseModalHandler } = bankListBottomSheetHandlers(
    infoModal,
    setInfoModal,
    beneficiaryDeleteMutate,
  );

  return (
    <View style={appStyles.safeAreaView}>
      <ScrollView styles={appStyles.safeAreaView}>
        <DefaultBankHeader />
        <BankList bankList={bankList ?? []} fromTransfer={fromTransfer} setBankForTransfer={setBankAccountNumber} />
      </ScrollView>
      {shouldShowFooter(t, fromTransfer, bankList, AMPLITUDE_EVENT_SOURCES.bank_page)}
      <BottomSheet
        onPressPrimary={() => onPressPrimaryBottomSHeet(bankRowPressed)}
        onPressSecondary={onPressSecondaryBottomSheet}
        loginInfo={infoModal}
        hasTwoButtons={infoModal.data.hasTwoButtons}
        onClose={onCloseModalHandler}
      />
    </View>
  );
}

export default GGBankListWithDefaultBank;
